<script>
	import * as shlex from "shlex";

	export let clipivot_core;

	let rowOrder = "index";
	$: rowOrderPresentation = (rowOrder === "index") ? "" : (rowOrder === "ascending") ? "--asc-rows" : "--desc-rows";
	let columnOrder = "ascending";
	$: colOrderPresentation = (columnOrder === "ascending") ? "" : (columnOrder === "descending") ? "--desc-cols" : "--index-cols";
	let ignoreEmpty = false;
	$: ignoreEmptyFlag = ignoreEmpty ? "-e" : "";
	let includeHeader = true;
	$: includeHeaderFlag = includeHeader ? "" : "--no-header";
	let delim = ",";
	$: delimPresent = (delim === ",") ? "" : (delim === "\\t") ? "-t" : `-d ${shlex.quote(delim)}`;
	let aggtype = "text";
	let allFuncs = [
		{name: "Count", value: "count"},
		{name: "Unique Count", value: "countunique"},
		{name: "Maximum", value: "max"},
		{name: "Mean", value: "mean"},
		{name: "Minimum", value: "min"},
		{name: "Minimum and Maximum", value: "minmax"},
		{name: "Mode", value: "mode"},
		{name: "Range", value: "range"},
		{name: "Sample Standard Deviation", value: "stddev"},
		{name: "Sum", value: "sum"}
	];
	let availableFunctions = allFuncs;
	$: if (aggtype === "text") {
		availableFunctions = allFuncs
		.filter(d => ["count", "countunique", "max", "min", "minmax"].includes(d.value));
	}
	$: if (aggtype === "numeric") {
		availableFunctions = allFuncs
		.filter(d => ["count", "countunique", "max", "mean", "median", "min", "minmax", "range", "stddev", "sum"].includes(d.value));
	}
	$: if (aggtype === "date") {
		availableFunctions = allFuncs
		.filter(d => ["count", "countunique", "max", "min", "minmax", "range"].includes(d.value));
	}
	let aggFunc = "count";
	$: numFormat = (aggtype === "numeric" && ["max", "min", "minmax"].includes(aggFunc)) ? "-N" : "";
	let dateFormat;
	$: if (aggtype !== "date") {dateFormat = undefined;}
	$: dateFmtPresent = (dateFormat === undefined) ? "" : `-F ${shlex.quote(dateFormat)}`;

	let inputFiles = [];
	$: inputFile = inputFiles[0];
	$: fileName = (inputFile === undefined) ? "" : shlex.quote(inputFile.name);

	let headerRow;
	let errorMsg;

	let pivotCols = [];
	let pivotRows = [];
	let pivotVal = 0;

	$: colPresent = (pivotCols.length === 0) ? "" : "--cols " + pivotCols.join(" ");
	$: rowPresent = (pivotRows.length === 0) ? "" : "--rows " + pivotRows.join(" ");
	$: valPresent = `-v ${pivotVal}`;

	// the filter stops there from being multiple spaces in the code section
	$: cliOpts = [
		colPresent,
		rowPresent,
		delimPresent,
		includeHeaderFlag,
		ignoreEmptyFlag,
		numFormat,
		dateFmtPresent,
		rowOrderPresentation,
		colOrderPresentation
	].filter(d => d !== "");

	$: cmd = `clipivot ${aggFunc} ${fileName} ${valPresent} ${cliOpts.join(' ')}`;

	async function parseHeader() {
		let headerResult = await clipivot_core.read_header(inputFile.stream(), delim);
		errorMsg = headerResult.errorMessage;
		// if you didn't get an error message
		if (headerResult.data !== null) {
			headerRow = headerResult.data[0];
		}
	}

	async function copyClipboard() {
		await navigator.clipboard.writeText(cmd);
	}

	let pivotResults;
	let numPivotRows;
	let numPivotCols;
	let loading = false;

	async function parsePivotTable() {
		if (!inputFile) { errorMsg = "You must add a file to run the pivot table";}
		numPivotCols = pivotCols.length;
		numPivotRows = pivotRows.length;
		loading = true;
		// set the number of pivot table rows  and cols so we can bold them and split them later
		return await clipivot_core.pivot_table(
			inputFile.stream(),
			aggFunc,
			delim,
			includeHeader,
			pivotRows,
			pivotCols,
			pivotVal,
			ignoreEmpty,
			rowOrder,
			columnOrder,
			aggtype,
			dateFormat
		)
		.then((data) => {
			loading = false;
			if (data.errorMessage !== null) {
			errorMsg = data.errorMessage;
			throw new Error(errorMsg);
			}
			else { 
				errorMsg = undefined;
				let blob = new Blob([data.csvContent], {type: "text/csv"});
				let blobHref = window.URL.createObjectURL(blob);
				return {
					data: data.data,
					csvHref: blobHref
				};
			}
		})
		.catch((err) => {});
	}

	function buildPivotTable(e) {
		e.preventDefault();
		pivotResults = parsePivotTable();
	}
</script>

<form>
	<div id="form-container">
		<div class="sorting">
			<fieldset>
				<legend>Row Order</legend>
				<p class="help">How do you want to sort the rows of the pivot table?</p>
				<div class="radio-fields">
					<label>
						<input type=radio bind:group={rowOrder} value={"index"}>
						Index Order
					</label>
					<label>
						<input type=radio bind:group={rowOrder} value={"ascending"}>
						Ascending Order
					</label>
					<label>
						<input type=radio bind:group={rowOrder} value={"descending"}>
						Descending Order
					</label>
				</div>
			</fieldset>
			<fieldset>
				<legend>Column Order</legend>
				<p class="help">How do you want to sort the columns of the pivot table?</p>
				<label>
					<input type=radio bind:group={columnOrder} value={"ascending"}>
					Ascending Order
				</label>
				<label>
					<input type=radio bind:group={columnOrder} value={"descending"}>
					Descending Order
				</label>
				<label>
					<input type=radio bind:group={columnOrder} value={"index"}>
					Index Order
				</label>
			</fieldset>
		</div>
		<div class="data-type">
		<fieldset>
			<legend>Data Type</legend>
			<p class="help">What type of data are you aggregating?</p>
			<label>
				<input type=radio bind:group={aggtype} value={"text"}>
				Text
			</label>
			<label>
				<input type=radio bind:group={aggtype} value={"numeric"}>
				Numeric
			</label>
			<label>
				<input type=radio bind:group={aggtype} value={"date"}>
				Date
			</label>
		</fieldset>
		</div>
		<div class="question-container">
			<div class="question-container-inner">
				<label for="aggfunc-select">
					What aggregation function do you want to use?
				</label>
				<select id="aggfunc-select" bind:value={aggFunc}>
					{#each availableFunctions as func}
					<option value={func.value}>{func.name}</option>
					{/each}
				</select>
			</div>
		</div>
		{#if aggtype === "date"}
		<label>Date Format
			<p class="help">Select a date format conforming to <a href="https://docs.rs/chrono/0.4.9/chrono/format/strftime/index.html">these specifications.</a></p>
			<input bind:value={dateFormat}>
		</label>
		{/if}
		<div class="ignore-handle-container">
			<div class="ignore-handle">
				<label>
					<input type=radio on:click={() => {ignoreEmpty = !ignoreEmpty;}} checked={ignoreEmpty}>
					Ignore null values in aggregation column
				</label>
				<label>
					<input type=radio on:click={() => {includeHeader = !includeHeader;}} checked={!includeHeader}>
					Ignore the header
				</label>
				<label>
					<input id="delimiter" bind:value={delim}>
					Delimiter
				</label>
			</div>
		</div>
		<div class="question-container">
			<div class="question-container-inner">
				<label for="file-upload">
					Add your file
				</label>
				<input type="file" id="file-upload" bind:files={inputFiles} on:change={parseHeader}>
			</div>
		</div>
		{#if headerRow}
		<div class="header-section">
			<div class="pivot-field">
				<fieldset>
					<legend>Rows</legend>
					<p class="help">Which columns do you want to serve as the rows for your pivot table?</p>
					{#each headerRow as header, count}
					<label>
						<input type=checkbox bind:group={pivotRows} value={count}>
						{includeHeader ? header : `Column ${count}`}
					</label>
					{/each}
				</fieldset>
			</div>
			<div class="pivot-field">
				<fieldset>
					<legend>Columns</legend>
					<p class="help">Which columns do you want to serve as the columns for your pivot table</p>
					{#each headerRow as header, count}
					<label>
						<input type=checkbox bind:group={pivotCols} value={count}>
						{includeHeader ? header : `Column ${count}`}
					</label>
					{/each}
				</fieldset>
			</div>
			<div class="pivot-field">
				<fieldset>
					<legend>Value</legend>
					<p class="help">Which column do you want to aggregate values over?</p>
					{#each headerRow as header, count}
					<label>
						<input type=radio bind:group={pivotVal} value={count}>
						{includeHeader ? header : `Column ${count}`}
					</label>
					{/each}
				</fieldset>
			</div>
		</div>
		{/if}
	</div>
	<div class="submit-section">
		<button type="submit" on:click={buildPivotTable}>Create pivot table</button>
	</div>
</form>
<div class="error-panel">
	{#if errorMsg}
	<pre class="inline-highlight">
		<code><b>Error:</b> {errorMsg}</code>
	</pre>
	{/if}
</div>
<div class="output-container">
	<div class="command-container">
		<pre id="clipivot-command" class="inline-highlight">
			<code>
				$ {cmd}
			</code>
			<div class="copy-icon" aria-label="Copy generated clipivot command to clipboard">
				<svg width="25px" on:click={copyClipboard} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
				  </svg>
			</div>
		</pre>
	</div>
	<div class="table-output-container">
	<div class="table-output">
		{#if pivotResults}
		{#await pivotResults}
		{#if loading}
			<div class="loading-container">
				<div class="loader-icon"></div>
				<div class="loader">Loading&hellip;</div>
			</div>
			{/if}
		{:then data}
		<div class="download-link">
			<button type="button"><a href={data.csvHref} download="pivot-table-download.csv">Download Results</a></button>
		</div>
		<div class="data-table">
		<table>
			{#if numPivotCols >= 2}
			<tr>
				{#each [...Array(Math.max(numPivotRows - 1, 0)).keys()] as _}
				<th></th>
				{/each}
				{#each data.data[0] as _}
				<th colspan={numPivotCols} scope="colgroup"></th>
				{/each}
			</tr>
			{/if}
				{#each data.data as row, rowCount}
					<tr class={rowCount % 2 === 0 ? "even-row" : "odd-row"}>
					{#each row as cell, colCount}
						{#if rowCount === 0}
							{#each [...Array(Math.max(numPivotRows - 1, 0)).keys()] as _}
								<th></th>
							{/each}
							{#each cell.split("_<sep>_") as headerColCell}
								<th scope="col" class={colCount % 2 === 0 ? "even-col" : "odd-col"}>{headerColCell}</th>
							{/each}
						{:else if colCount === 0}
							{#each cell.split("_<sep>_") as headerRowCell}
								<th scope="row" class={colCount % 2 === 0 ? "even-col" : "odd-col"}>{headerRowCell}</th>
							{/each}
						{:else}
							<td colspan={numPivotCols} class={colCount % 2 === 0 ? "even-col" : "odd-col"}>{cell}</td>
						{/if}
					{/each}
					</tr>
				{/each}
		</table>
		</div>
		{/await}
		{/if}
	</div>
	</div>
</div>

<style>
	form {
		width: 95%;
		margin: auto;
		border: 1px dashed rgb(150,150,150);
		padding: 3px;
	}

	code, .copy-icon {
		display: inline;
	}

	.copy-icon > svg {
		background-color: rgb(215, 217, 249);
		padding: 1px;
		border-radius: 3px;
		transition: 0.5s;
	}

	.copy-icon > svg:hover {
		cursor: pointer;
		background-color: rgb(153, 158, 255);
	}

	.sorting {
		display: flex;
		justify-content: center;
	}

	.sorting > fieldset, .ignore-handle > label {
		display: inline;
	}

	#delimiter {
		padding: 0;
		max-width: 25px;
	}
	
	label {
		display: inline-block;
	}

	.command-container {
		display: flex;
		justify-content: center;
	}

	.error-panel {
		color: red;
		text-align: center;
		padding-top: 1rem;
	}

	.error-panel > pre {
		border: 2px dashed;
		padding: 7px;
	}

	.header-section {
		display: flex;
	}

	.pivot-field > fieldset > label {
		display: block;
	}

	.submit-section {
		display: flex;
		justify-content: center;
	}

	.ignore-handle-container {
		display: flex;
		justify-content: center;
	}

	.ignore-handle {
		display: flex;
		flex-direction: column;
	}

	.even-row {
		background-color: #A6ABF2;
	}

	.odd-row {
		background-color: #D7D9F9;
	}

	.data-type {
		display: flex;
		justify-content: center;
	}

	.question-container {
		display: flex;
		justify-content: center;
	}
	.question-container-inner {
		display: flex;
		flex-direction: column;
	}

	.loader {
		font-size: 1.5rem;
	}

	.loader-icon {
		display: inline-block;
		border: 1rem solid white;
		border-radius: 50%;
		width: 100px;
		height: 100px;
		-webkit-animation: spin 1.7s linear infinite;
 		animation: spin 1.7s linear infinite;
		border-top: 1rem solid rgb(153, 158, 255);
	}

	@-webkit-keyframes spin {
  		0% { -webkit-transform: rotate(0deg); }
  		100% { -webkit-transform: rotate(360deg); }
	}

	@keyframes spin {
		0% { transform: rotate(0deg);}
		100% { transform: rotate(360deg);}
	}

	th, td {
		padding: 1ch;
	}

	.table-output {
		display: flex;
		flex-direction: column;
	}

	.table-output-container {
		display: flex;
		justify-content: center;
	}
</style>